import { useRef, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { MapPin, Video, Check, Calendar } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { appointmentDateParts } from '@/lib/format';
import type { ScheduleDay, ScheduleProvider, ScheduleAppointment } from '@vagusx/clients';
import { useProviderColors } from './hooks/use-provider-colors';
import {
  APPOINTMENT_STATUS,
  APPOINTMENT_STATUS_LABELS,
  APPOINTMENT_STATUS_COLORS,
} from './hooks/use-schedule';

interface TableViewProps {
  days: ScheduleDay[];
  providers: ScheduleProvider[];
  isLoading?: boolean;
  onDayVisible?: (date: Date) => void;
  onAppointmentClick?: (appointmentId: string) => void;
}

export function TableView({
  days,
  providers,
  isLoading,
  onDayVisible,
  onAppointmentClick,
}: TableViewProps): React.ReactElement {
  const providerColors = useProviderColors(providers);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const dayRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());

  // Set up intersection observer to detect which day is currently visible
  useEffect(() => {
    if (!onDayVisible || !scrollContainerRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          const topEntry = visibleEntries.sort(
            (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
          )[0];
          const dateStr = topEntry?.target.getAttribute('data-date');
          if (dateStr) {
            onDayVisible(parseISO(dateStr));
          }
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '-10px 0px -90% 0px',
        threshold: 0,
      }
    );

    dayRefs.current.forEach((element) => {
      observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [days, onDayVisible]);

  const setDayRef = useCallback((dateStr: string, element: HTMLTableRowElement | null) => {
    if (element) {
      dayRefs.current.set(dateStr, element);
    } else {
      dayRefs.current.delete(dateStr);
    }
  }, []);

  if (isLoading) {
    return <TableViewSkeleton />;
  }

  if (days.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground py-20">
        <Calendar className="h-12 w-12 mb-4 opacity-50" />
        <p className="text-lg font-medium">No appointments found</p>
        <p className="text-sm">Try adjusting your filters or date range</p>
      </div>
    );
  }

  // Flatten all appointments with their day information
  const rows: Array<
    | { type: 'day'; date: string }
    | { type: 'appointment'; appointment: ScheduleAppointment; dateStr: string }
  > = [];

  days.forEach((day) => {
    rows.push({ type: 'day', date: day.date });
    day.appointments.forEach((appointment) => {
      rows.push({ type: 'appointment', appointment, dateStr: day.date });
    });
  });

  return (
    <ScrollArea className="h-full" ref={scrollContainerRef}>
      <Table>
        <TableHeader className="sticky top-0 bg-background z-20">
          <TableRow>
            <TableHead className="w-[100px]">Time</TableHead>
            <TableHead>Patient</TableHead>
            <TableHead>Provider</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Location</TableHead>
            <TableHead className="w-[100px]">Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, index) => {
            if (row.type === 'day') {
              const date = parseISO(row.date);
              const dayAppointments = days.find((d) => d.date === row.date)?.appointments ?? [];

              return (
                <TableRow
                  key={`day-${row.date}`}
                  ref={(el) => setDayRef(row.date, el)}
                  data-date={row.date}
                  className="bg-muted/50 sticky top-10 z-10"
                >
                  <TableCell colSpan={6} className="py-2">
                    <div className="flex items-center justify-between">
                      <span className="font-semibold">{format(date, 'EEEE, MMMM d, yyyy')}</span>
                      <span className="text-sm text-muted-foreground">
                        {dayAppointments.length}{' '}
                        {dayAppointments.length === 1 ? 'appointment' : 'appointments'}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              );
            }

            const { appointment, dateStr } = row;
            // The clinic's clock, matching the day header (grouped by the clinic's day).
            const tz = appointment.location?.timezone;
            const start = appointmentDateParts(appointment.startTime, tz);
            const end = appointmentDateParts(appointment.endTime, tz);
            const color = appointment.provider
              ? providerColors.get(appointment.provider.id)
              : undefined;
            const statusColors = APPOINTMENT_STATUS_COLORS[appointment.status] ?? {
              bg: 'bg-gray-100',
              text: 'text-gray-700',
            };
            const statusLabel = APPOINTMENT_STATUS_LABELS[appointment.status] ?? 'Unknown';

            return (
              <TableRow
                key={appointment.id}
                onClick={() => onAppointmentClick?.(appointment.id)}
                className={cn('cursor-pointer hover:bg-muted/50 transition-colors', color?.bg)}
              >
                <TableCell className="font-medium">
                  <div className="text-sm">{start.time}</div>
                  <div className="text-xs text-muted-foreground">
                    {end.time}
                    {start.zoneLabel && ` ${start.zoneLabel}`}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="font-medium">
                    {appointment.patient
                      ? `${appointment.patient.firstName} ${appointment.patient.lastName}`
                      : 'Unknown'}
                  </div>
                </TableCell>
                <TableCell>
                  <div className={cn('flex items-center gap-2', color?.text)}>
                    {color && <div className={cn('h-2 w-2 rounded-full', color.dot)} />}
                    {appointment.provider ? `${appointment.provider.lastName}` : 'Unknown'}
                  </div>
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {appointment.appointmentType?.name ?? appointment.reasonForVisit ?? '-'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    {appointment.isTelehealth ? (
                      <>
                        <Video className="h-3.5 w-3.5 text-blue-500" />
                        <span>Telehealth</span>
                      </>
                    ) : appointment.location ? (
                      <>
                        <MapPin className="h-3.5 w-3.5" />
                        <span>{appointment.location.name}</span>
                      </>
                    ) : (
                      '-'
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  <Badge
                    className={cn('text-xs', statusColors.bg, statusColors.text)}
                    variant="outline"
                  >
                    {appointment.status === APPOINTMENT_STATUS.BOOKED && (
                      <Check className="h-3 w-3 mr-1" />
                    )}
                    {statusLabel}
                  </Badge>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </ScrollArea>
  );
}

function TableViewSkeleton(): React.ReactElement {
  return (
    <div className="p-4">
      <Skeleton className="h-10 w-full mb-2" />
      {[1, 2, 3, 4, 5].map((i) => (
        <Skeleton key={i} className="h-14 w-full mb-2" />
      ))}
    </div>
  );
}
